import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { XCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const PositionsTable = ({ positions = [], onRefresh, compact = false }) => {
  const handleClose = async (posId, symbol) => {
    try {
      const res = await fetch(`${API}/api/positions/${posId}/close`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Closed ${symbol} position. P&L: ${data.pnl_pct >= 0 ? '+' : ''}${data.pnl_pct}%`);
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to close position');
      }
    } catch {
      toast.error('Network error');
    }
  };

  if (positions.length === 0) {
    return (
      <div className="p-6 text-center text-[hsl(var(--muted-foreground))]">
        <p className="text-sm">No active positions. Buy a recommended stock to start tracking.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="positions-table">
      <Table className="w-full">
        <TableHeader>
          <TableRow className="border-b border-[hsl(var(--border))]">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Symbol</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Entry</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Current</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Target</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Stop</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">P&L</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Status</TableHead>
            {!compact && <TableHead className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {positions.map((pos) => {
            const pnl = pos.pnl_pct || 0;
            const isProfit = pnl >= 0;
            return (
              <TableRow
                key={pos._id}
                className="hover:bg-[hsl(var(--accent))] transition-colors duration-150 border-b border-[hsl(var(--border))]"
                data-testid="positions-table-row"
              >
                <TableCell>
                  <div>
                    <span className="font-mono font-semibold text-sm">{pos.symbol}</span>
                    {!compact && <p className="text-xs text-[hsl(var(--muted-foreground))]">{pos.company_name}</p>}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm tabular-nums">${pos.buy_price?.toFixed(2)}</TableCell>
                <TableCell className="font-mono text-sm tabular-nums">${pos.current_price?.toFixed(2)}</TableCell>
                <TableCell className="font-mono text-sm tabular-nums text-emerald-400">${pos.target_price?.toFixed(2)}</TableCell>
                <TableCell className="font-mono text-sm tabular-nums text-red-400">${pos.stop_loss?.toFixed(2)}</TableCell>
                <TableCell>
                  <span className={`font-mono text-sm font-semibold tabular-nums flex items-center gap-1 ${
                    isProfit ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {isProfit ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
                  </span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`text-[10px] uppercase ${
                      pos.status === 'active' ? 'text-emerald-400 border-emerald-500/30' :
                      pos.status === 'target_hit' ? 'text-emerald-400 border-emerald-500/30' :
                      pos.status === 'stop_loss' ? 'text-red-400 border-red-500/30' :
                      'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
                    }`}
                  >
                    {pos.status === 'target_hit' ? 'Target Hit' : pos.status === 'stop_loss' ? 'Stop Hit' : pos.status}
                  </Badge>
                </TableCell>
                {!compact && (
                  <TableCell>
                    {pos.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClose(pos._id, pos.symbol)}
                        className="text-[hsl(var(--muted-foreground))] hover:text-red-400 text-xs"
                        data-testid="positions-table-close-position"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Close
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
